import { FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import {
  formatDividendAmount,
  formatDividendPerShare,
} from '../../dividends/utils/format-dividend-amount.util';
import { PayoutGroup, statusStyleMap } from './dividends-ledger-item';

const Row: FC<{ green?: boolean; label: ReactNode; value: ReactNode }> = ({
  green,
  label,
  value,
}) => (
  <div className="flex items-center justify-between gap-3 border-b border-gray-100 py-3 pl-3 pr-4 last:border-b-0">
    <span className="shrink-0 text-sm font-[450] text-gray-700">{label}</span>
    <span
      className={twMerge(
        'min-w-0 truncate text-right text-sm',
        green ? 'text-forest-600' : 'text-gray-700',
      )}
    >
      {value}
    </span>
  </div>
);

type Props = {
  currencySymbol: string;
  group: PayoutGroup;
  onBack: () => void;
};

// Full-screen mobile detail for a payout (replaces the centre modal on phones).
export const DividendsLedgerMobileDetail: FC<Props> = ({ currencySymbol, group, onBack }) => {
  const style = statusStyleMap[group.status];

  const formatAmount = (value: number) => `${currencySymbol} ${formatDividendAmount(value)}`;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-50 p-3">
      <div className="flex flex-col overflow-hidden rounded-md bg-white px-3 pb-4 shadow-sm">
        <div className="flex items-center gap-2 border-b border-gray-100 py-4 text-xs font-[450]">
          <span className="text-brand-700">
            <AppFormattedMessage id="MY_HOLDINGS" />
          </span>
          <span className="text-gray-400">/</span>
          <span className="truncate text-gray-400">{group.name}</span>
        </div>

        <div className="flex h-[72px] items-center justify-between border-b border-gray-100 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <button
              className="flex size-9 shrink-0 items-center justify-center rounded-full border border-gray-100 shadow-sm"
              onClick={onBack}
              type="button"
            >
              <svg
                className="size-4"
                fill="none"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.833 10H4.167M4.167 10l5 5M4.167 10l5-5"
                  stroke="#344054"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                />
              </svg>
            </button>
            <span className="min-w-0 truncate text-base font-semibold text-gray-700">
              {group.name}
            </span>
          </div>
          <span
            className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-xs font-[550] ${style.bg} ${style.text}`}
          >
            {style.label}
          </span>
        </div>

        <div className="flex flex-col gap-4 pt-4">
          {group.items.map((item, index) => (
            <div
              className="flex flex-col overflow-hidden rounded-md border border-gray-100 bg-white shadow-sm"
              key={index}
            >
              <div className="border-b border-gray-100 px-3 py-4">
                <span className="text-base font-semibold text-gray-700">
                  {item.shareClassName ?? '—'}
                </span>
              </div>
              <Row
                label={<AppFormattedMessage id="DIVIDEND_PER_SHARE" />}
                value={
                  item.amountPerShare !== null ? formatDividendPerShare(item.amountPerShare) : '—'
                }
              />
              <Row
                label={<AppFormattedMessage id="SHARE_AMOUNT" />}
                value={
                  item.stakeholderShares !== null
                    ? item.stakeholderShares.toLocaleString('en-US')
                    : '—'
                }
              />
              <Row
                green
                label={<AppFormattedMessage id="PAYOUT_FOR_SHARE_CLASS" />}
                value={formatAmount(item.itemAmount)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
